import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import { CollectionContact, CollectionInvoiceDocument } from '../model';
import { ContactEmailAction } from '../hooks/useToggleCollectionClientContactEmail';
import OneOffEmailModal from './OneOffEmailModal';
import MandatoryEmailNoteModal from './MandatoryEmailNoteModal';
import { sortContactsForDisplay, VISIBLE_CONTACT_LIMIT } from '../utils/sortContacts';
import { useCollectionConfig } from '../../config/hooks/useCollectionConfig';

interface Props {
  contacts: CollectionContact[];
  recipientEmails: string[];
  onToggleContact: (conIdNo: string, action: ContactEmailAction) => Promise<void>;
  disabled?: boolean;
  clientId: number;
  location: string;
  invoiceDocuments: CollectionInvoiceDocument[];
  onEmailSent: () => void;
}

const buildRecipientSet = (recipientEmails: string[]): Set<string> =>
  new Set(recipientEmails.map((email) => email.toLowerCase()));

const contactIsSelected = (contact: CollectionContact, recipientSet: Set<string>): boolean =>
  contact.emails.some((email) => recipientSet.has(email.toLowerCase()));

const contactActionButtonSx = {
  width: 32,
  height: 32,
  p: 0,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'primary.dark',
  bgcolor: 'primary.main',
  color: 'common.white',
  flexShrink: 0,
  transition: 'transform 0.2s ease, background-color 0.2s ease',
  '& .MuiSvgIcon-root': {
    fontSize: '1.125rem',
    transition: 'transform 0.2s ease',
  },
  '&:hover': {
    bgcolor: 'primary.dark',
    transform: 'scale(1.05)',
    '& .MuiSvgIcon-root': {
      transform: 'scaleX(-1)',
    },
  },
  '&.Mui-disabled': {
    bgcolor: 'action.disabledBackground',
    color: 'action.disabled',
    borderColor: 'divider',
  },
} as const;

interface ContactActionButtonProps {
  title: string;
  icon: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
}

const ContactActionButton: React.FC<ContactActionButtonProps> = ({ title, icon, disabled, onClick }) => (
  <Tooltip title={title}>
    <span>
      <IconButton size="small" disabled={disabled} onClick={onClick} aria-label={title} sx={contactActionButtonSx}>
        {icon}
      </IconButton>
    </span>
  </Tooltip>
);

interface ContactCardProps {
  contact: CollectionContact;
  recipientSet: Set<string>;
  disabled?: boolean;
  onRequestToggle: (contact: CollectionContact, action: ContactEmailAction) => void;
  onComposeEmail: (contact: CollectionContact, email: string) => void;
}

const ContactCard: React.FC<ContactCardProps> = ({
  contact,
  recipientSet,
  disabled,
  onRequestToggle,
  onComposeEmail,
}) => {
  const selected = contactIsSelected(contact, recipientSet);
  const canToggle = !!contact.con_id_no && contact.emails.length > 0 && !disabled;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderWidth: selected ? 2 : 1,
        borderRadius: 1,
        p: 1.5,
        height: '100%',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {contact.name || '—'}
          </Typography>
          {contact.job_title && (
            <Typography variant="caption" color="text.secondary">
              {contact.job_title}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
          {contact.is_primary === 'A' && <Chip label="Primary" size="small" color="success" />}
          {contact.is_billing_primary === 'A' && <Chip label="Billing" size="small" color="primary" />}
          {contact.inactive && <Chip label="Inactive" size="small" variant="outlined" />}
          <Tooltip title={selected ? 'Remove from collection emails' : 'Add to collection emails'}>
            <span>
              <IconButton
                size="small"
                color={selected ? 'primary' : 'default'}
                disabled={!canToggle}
                onClick={() => contact.con_id_no && onRequestToggle(contact, selected ? 'remove' : 'add')}
              >
                {selected ? <PlaylistRemoveIcon fontSize="small" /> : <PlaylistAddCheckIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
        {contact.phones.length > 0 ? (
          contact.phones.map((phone) => (
            <Box key={phone} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ContactActionButton
                title={contact.con_id_no ? 'Call contact (coming soon)' : 'Contact has no Servman id'}
                icon={<PhoneIcon />}
                disabled={!contact.con_id_no || disabled}
              />
              <Typography variant="body2">{phone}</Typography>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            No phone
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.75 }}>
        {contact.emails.length > 0 ? (
          contact.emails.map((email) => (
            <Box key={email} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <ContactActionButton
                title={contact.con_id_no ? 'Send one-off email' : 'Contact has no Servman id'}
                icon={<EmailIcon />}
                disabled={!contact.con_id_no || disabled}
                onClick={() => onComposeEmail(contact, email)}
              />
              <Chip label={email} size="small" />
            </Box>
          ))
        ) : (
          <Chip label="No email" size="small" color="error" variant="outlined" />
        )}
      </Box>
    </Box>
  );
};

const CollectionClientContactsCard: React.FC<Props> = ({
  contacts,
  recipientEmails,
  onToggleContact,
  disabled,
  clientId,
  location,
  invoiceDocuments,
  onEmailSent,
}) => {
  const [pendingContact, setPendingContact] = useState<CollectionContact | null>(null);
  const [pendingAction, setPendingAction] = useState<ContactEmailAction | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [composeTarget, setComposeTarget] = useState<{ email: string; name: string | null } | null>(null);
  const [noteContext, setNoteContext] = useState<{ toEmail: string; subject: string } | null>(null);
  const [showAllContacts, setShowAllContacts] = useState(false);

  const { data: configResp } = useCollectionConfig(location);
  const sendingDisabled = configResp?.data?.sending_enabled === false;

  useEffect(() => {
    setShowAllContacts(false);
  }, [clientId]);

  const sortedContacts = useMemo(
    () => sortContactsForDisplay(contacts, recipientEmails),
    [contacts, recipientEmails],
  );
  const visibleContacts = showAllContacts
    ? sortedContacts
    : sortedContacts.slice(0, VISIBLE_CONTACT_LIMIT);
  const hiddenContactCount = sortedContacts.length - VISIBLE_CONTACT_LIMIT;

  const recipientSet = buildRecipientSet(recipientEmails);

  const handleRequestToggle = (contact: CollectionContact, action: ContactEmailAction) => {
    setPendingContact(contact);
    setPendingAction(action);
  };

  const handleComposeEmail = (contact: CollectionContact, email: string) => {
    setComposeTarget({ email, name: contact.name });
  };

  const handleCloseDialog = () => {
    if (isConfirming) return;
    setPendingContact(null);
    setPendingAction(null);
  };

  const handleConfirm = async () => {
    if (!pendingContact?.con_id_no || !pendingAction) return;

    setIsConfirming(true);
    try {
      await onToggleContact(pendingContact.con_id_no, pendingAction);
      setPendingContact(null);
      setPendingAction(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const dialogTitle =
    pendingAction === 'remove'
      ? 'Remove from collection emails?'
      : 'Add to collection emails?';

  const dialogBody =
    pendingContact && pendingAction ? (
      <>
        {pendingAction === 'remove' ? 'Remove' : 'Add'}{' '}
        <strong>{pendingContact.name || 'this contact'}</strong>
        {pendingAction === 'remove' ? ' from' : ' to'} collection notices?
        {pendingContact.emails.length > 0 && (
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {pendingContact.emails.map((email) => (
              <li key={email}>
                <Typography variant="body2">{email}</Typography>
              </li>
            ))}
          </Box>
        )}
      </>
    ) : null;

  if (!contacts || contacts.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No contacts.
      </Typography>
    );
  }

  return (
    <>
      <Grid container spacing={1.5}>
        {visibleContacts.map((contact, index) => (
          <Grid item xs={12} md={6} key={contact.con_id_no || `contact-${index}`}>
            <ContactCard
              contact={contact}
              recipientSet={recipientSet}
              disabled={disabled || isConfirming}
              onRequestToggle={handleRequestToggle}
              onComposeEmail={handleComposeEmail}
            />
          </Grid>
        ))}
      </Grid>

      {sortedContacts.length > VISIBLE_CONTACT_LIMIT && (
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={() => setShowAllContacts((prev) => !prev)}>
            {showAllContacts
              ? 'Show fewer contacts'
              : `Show more contacts (${hiddenContactCount} hidden)`}
          </Button>
        </Box>
      )}

      <Dialog open={pendingContact !== null} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">{dialogBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={isConfirming}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} variant="contained" disabled={isConfirming} autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {composeTarget && (
        <OneOffEmailModal
          open={composeTarget !== null && noteContext === null}
          clientId={clientId}
          toEmail={composeTarget.email}
          contactName={composeTarget.name}
          sendingDisabled={sendingDisabled}
          invoiceDocuments={invoiceDocuments}
          onClose={() => setComposeTarget(null)}
          onSent={(info) => setNoteContext(info)}
        />
      )}

      {noteContext && (
        <MandatoryEmailNoteModal
          open={noteContext !== null}
          clientId={clientId}
          prefill={`One-off email sent to ${noteContext.toEmail} — Subject: ${noteContext.subject}`}
          onCompleted={() => {
            setNoteContext(null);
            setComposeTarget(null);
            onEmailSent();
          }}
        />
      )}
    </>
  );
};

export default CollectionClientContactsCard;
